/// <amd-module name="Release/DeploymentsSection/DeploymentComponent" />
import { VssComponent, IVssComponentContext } from "VSS/Platform/Layout";
import { localeFormat } from "VSS/Core/Util/String";
import { FPSLink } from "VSS/Features/PlatformUI/FPSLink";
import { IVssPerformanceService } from "VSS/Platform/Performance";

import * as React from "react";

import { Button } from "VSSUI/Button";
import { css } from "VSSUI/Util";
import { FocusZone, FocusZoneDirection } from "OfficeFabric/FocusZone";
import { Link } from "VSSUI/Link";
import { IListItemDetails, List, ListItem } from "VSSUI/List";
import { ArrayItemProvider } from "VSSUI/Utilities/Provider";
import { Icon } from "VSSUI/Icon";

import { DeploymentsComponentStore, Deployments, TriggerIssue, IDeploymentsState } from "Release/DeploymentsSection/Stores/DeploymentsComponentStore";
import { DeploymentsComponentActionsCreator } from "Release/DeploymentsSection/Actions/DeploymentsComponentActionsCreator";
import { DeploymentsSignalRHub } from "Release/DeploymentsSection/SignalRHub";
import { EnvironmentsChain, IActiveReleaseEnvironmentsCellProps } from "Release/EnvironmentNode/EnvironmentChain";
import * as RMContracts from "Release/Client/Contracts/Release";
import * as Resources from "Release/DeploymentsSection/Resources";

export interface IDeploymentsProp {
    actionsCreator: DeploymentsComponentActionsCreator;
    store: DeploymentsComponentStore;
    signalRHub: DeploymentsSignalRHub;
}

export const enum DeploymentsSummaryPerfConstants {
    Area = "ReleaseManagement",
    Scenario = "ReleaseManagement.DeploymentsSection.Load"
}

export class DeploymentsComponent extends VssComponent<IDeploymentsProp, IDeploymentsState> {
    private _actionsCreator: DeploymentsComponentActionsCreator;
    private _store: DeploymentsComponentStore;
    private _signalRHub: DeploymentsSignalRHub;
    private loggedPerfEvent: boolean;

    constructor(props: IDeploymentsProp, context: IVssComponentContext) {
        super(props, context);
        this._store = props.store;
        this._actionsCreator = props.actionsCreator;
        this._signalRHub = props.signalRHub;
        if (this._store) {
            this.state = this._store.getState();
        } else {
            this.state = DeploymentsComponentStore.getDefaultState();
        }
    }

    public componentDidMount(): void {
        this._store.addChangedListener(this._onStoreChanged);
        this._attachSignalRListener();

        if (!this.loggedPerfEvent) {
            const performanceService = this.context.pageContext.getService<IVssPerformanceService>("IVssPerformanceService");
            performanceService.endScenario(DeploymentsSummaryPerfConstants.Area, DeploymentsSummaryPerfConstants.Scenario, false);

            this.loggedPerfEvent = true;
        }
    }

    public componentWillReceiveProps(props: IDeploymentsProp) {
        this._signalRHub = !this._signalRHub ? props.signalRHub : this._signalRHub;
    }

    public render(): JSX.Element | undefined {
        this._showDeploymentsCountSeparator = false;

        return (
            <div className="deployments-section">
                <h3 className="deployments-title">{Resources.Title}</h3>

                <div className="deployments-summary-count">
                    {this._getDeploymentsCountComponent(this.state.failedDeploymentsCount, Resources.Failed)}
                    {this._getDeploymentsCountComponent(this.state.inProgressDeploymentsCount, Resources.InProgress)}
                    {this._getDeploymentsCountComponent(this.state.partiallySucceededCount, Resources.PartiallySucceeded)}
                    {this._getDeploymentsCountComponent(this.state.succeededDeploymentsCount, Resources.Succeeded)}
                </div>

                {this.state.deployments.length === 0 && (
                    <div>
                        <span className="no-deployments-text summary-secondary-text">{Resources.NoDeploymentsMessage}</span>
                    </div>
                )}

                <FocusZone direction={FocusZoneDirection.vertical}>
                    <div data-is-scrollable={true}>
                        <List
                            className="deployments-list"
                            itemProvider={new ArrayItemProvider(this.state.deployments)}
                            renderRow={this._onRenderCell}
                        />
                    </div>
                </FocusZone>

                {this.state.deployments.length > 0 && this.state.isShowMoreVisible && (
                    <div className="deployments-list-expand-buttons">
                        <Link
                            href="#"
                            className="summary-secondary-text"
                            onClick={() => {
                                this._actionsCreator.incrementRenderIndex();
                            }}
                        >
                            {Resources.ShowMore}
                        </Link>
                        <span className={"separator"}> | </span>
                        <Link
                            href="#"
                            className="summary-secondary-text"
                            onClick={() => {
                                this._actionsCreator.maximizeRenderIndex();
                            }}
                        >
                            {localeFormat(Resources.ViewAll, this.state.deployments.length)}
                        </Link>
                    </div>
                )}

                {this.state.deployments.length > 0 &&
                    !this.state.isShowMoreVisible &&
                    this.state.deployments.length !== this.state.defaultRenderCount && (
                        <div className=" deployments-list-expand-buttons">
                            <Link
                                href="#"
                                className="summary-secondary-text"
                                onClick={() => {
                                    this._actionsCreator.resetView();
                                }}
                            >
                                {Resources.ShowLess}
                            </Link>
                        </div>
                    )}

                {this._getIssuesComponent(this.state.triggerIssues, this.state.expandIssues)}
            </div>
        );
    }

    public componentWillUnmount(): void {
        this._store.removeChangedListener(this._onStoreChanged);
        this._store.dispose();
    }

    private _onStoreChanged = (): void => {
        this.setState(this._store.getState());
        this._attachSignalRListener();
    };

    private _attachSignalRListener() {
        this.state.deployments.forEach(deployment => {
            let activeEnvironments = deployment.environments.filter(environment => {
                if (
                    environment.status != RMContracts.EnvironmentStatus.Canceled &&
                    environment.status != RMContracts.EnvironmentStatus.PartiallySucceeded &&
                    environment.status != RMContracts.EnvironmentStatus.Rejected &&
                    environment.status != RMContracts.EnvironmentStatus.Succeeded
                ) {
                    return environment;
                }
            });

            if (this._signalRHub) {
                activeEnvironments.length > 0
                    ? this._signalRHub.watchRelease(deployment.projectId, deployment.id)
                    : this._signalRHub.stopWatchingRelease(deployment.projectId, deployment.id);
            }
        });
    }

    private _getDeploymentsCountComponent(deploymentsCount: number, deploymentStatus: string): JSX.Element | null {
        if (deploymentsCount === 0) {
            return null;
        }

        let countComponent = (
            <span className="summary-secondary-text">
                {this._showDeploymentsCountSeparator && <span className="separator">/</span>}
                <span className={this._getClassNameByDeploymentStatus(deploymentStatus)}>
                    {localeFormat(Resources.DeploymentsCountLabel, deploymentsCount, deploymentStatus)}
                </span>
            </span>
        );
        this._showDeploymentsCountSeparator = true;

        return countComponent;
    }

    private _getDeploymentsComponent(
        environments: Deployments,
        isFavorite: boolean = false,
        index: number,
        details: IListItemDetails<Deployments>
    ): JSX.Element {
        return (
            <ListItem details={details} index={index} key={"list-item" + index}>
                <div className="deployments-list-element">
                    <div data-is-focusable={true}>
                        {isFavorite && <Icon iconName="FavoriteStarFill" className={css("favorite-icon", "favorite-icon-filled")} />}
                        <div className="release-definition-header">
                            <FPSLink href={environments.rdUrl} className="defintion-name" telemetrySource="deployments-summary-view-definition-link">
                                {environments.rdName}
                            </FPSLink>
                            /
                            <FPSLink href={environments.releaseUrl} className="release-name" telemetrySource="deployments-summary-view-release-link">
                                {environments.releaseName}
                            </FPSLink>
                        </div>
                    </div>

                    {this._renderActiveReleaseEnvironmentsChain(environments)}
                </div>
            </ListItem>
        );
    }

    private _getIssuesComponent(issues: TriggerIssue[], expandIssues: boolean): JSX.Element | null {
        if (this.state.triggerIssues.length === 0) {
            return null;
        }

        return (
            <div className="issues-section">
                <div className="issues-section-header">
                    <h4 className="summary-secondary-text">{localeFormat(Resources.IssuesHeaderText, issues.length)}</h4>
                    <Button
                        subtle={true}
                        className="expand-issues"
                        iconProps={{
                            iconName: expandIssues ? "ChevronUp" : "ChevronDown",
                            className: "chevron-icon"
                        }}
                        onClick={() => {
                            this._toggleExpandIssues();
                        }}
                    />
                </div>
                {expandIssues &&
                    issues.map((issue: TriggerIssue, index: number) => {
                        return (
                            <div key={index} className="issues-section-content">
                                <div className="release-definition-header">
                                    <FPSLink
                                        href={issue.releaseDefinitionUrl}
                                        className="defintion-name"
                                        telemetrySource="deployments-summary-view-issue-definition-link"
                                    >
                                        {issue.releaseDefinitionName}
                                    </FPSLink>
                                </div>
                                {issue.isError && <span className="error-icon bowtie-icon bowtie-status-failure" />}
                                {issue.isWarning && <span className="warning-icon bowtie-icon bowtie-status-warning" />}
                                <span className="summary-secondary-text">{issue.issueMessage}</span>
                            </div>
                        );
                    })}
            </div>
        );
    }

    private _toggleExpandIssues(): void {
        this._actionsCreator.toggleExpandIssues();
    }

    private _renderActiveReleaseEnvironmentsChain(environments: Deployments): JSX.Element {
        let environmentChainProps: IActiveReleaseEnvironmentsCellProps = {
            cssClass: "deployments-rel-env-chain",
            environments: environments.environments,
            releaseEnvironmentNodeCss: "deployments-rel-env-node",
            definitionEnvironmentCurrentReleaseMap: this._createMap(environments.environments),
            releaseId: environments.id,
            definitionId: environments.rdId,
            envNodeWidth: this._envChainWidth,
            projectId: environments.projectId,
            onExpandClick: (widthOnExpand: number) => {}
        };

        return <EnvironmentsChain {...environmentChainProps} />;
    }

    private _onRenderCell = (index: number, item: Deployments, details: IListItemDetails<Deployments>): JSX.Element => {
        if (index >= this.state.renderCount) {
            return <div/>;
        }

        return this._getDeploymentsComponent(item, item.isFavorite, index, details);
    };

    private _createMap(environments: RMContracts.ReleaseEnvironment[]): { [key: number]: number } {
        let map: { [key: number]: number } = {};
        if (!environments) {
            return map;
        }

        environments.map((environment: RMContracts.ReleaseEnvironment) => {
            map[environment.definitionEnvironmentId] = environment.releaseId;
        });

        return map;
    }

    private _getClassNameByDeploymentStatus(deploymentStatus: string): string {
        switch (deploymentStatus) {
            case Resources.Failed:
                return "failed";
            case Resources.InProgress:
                return "inprogress";
            case Resources.PartiallySucceeded:
                return "partiallysucceeded";
            case Resources.Succeeded:
                return "succeeded";
            default:
                return "";
        }
    }

    private _showDeploymentsCountSeparator: boolean = false;
    private _envChainWidth: number = 96;
}
